import { Component, OnInit, Input } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { addDays } from 'date-fns';
import { TissueCollectionType } from '../../../../shared/services/request.service';
import { ProjectedTaskConfiguration } from '../../../../shared/services/projected-task-configuration.service';
import { ExperimentDetailsFormModel } from '../../../services/request-form.service';
import { SettingsService } from 'research.web.settings';
import { Settings } from '../../../../settings';

@Component({
  selector: 'app-sampling-needs-form',
  templateUrl: './sampling-needs-form.component.html',
  styleUrls: ['./sampling-needs-form.component.css']
})
export class SamplingNeedsFormComponent implements OnInit {
  
  @Input() public projectedTaskConfigurations: ProjectedTaskConfiguration[] = [];
  @Input() public tissueTypes: SamplingTissueTypeViewModel[] = [];
  @Input() public assays: SamplingAssayViewModel[] = [];
  @Input() public tissueCollectionTypes: SamplingTissueCollectionTypeViewModel[] = [];
  @Input() public growthStages: SamplingGrowthStageViewModel[] = [];
  @Input() public requestDetails: ExperimentDetailsFormModel;
  @Input() samplingNeeds: FormGroup;

  public GROWTH_STAGE: SamplingDateCalculationStrategy = SamplingDateCalculationStrategy.GrowthStage;
  public DAYS_AFTER_PLANTING: SamplingDateCalculationStrategy = SamplingDateCalculationStrategy.DaysAfterPlanting;
  public settings: Settings;

  public get assay(): FormControl {
    return <FormControl> this.samplingNeeds.get('assay');
  }

  public get tissueType(): FormControl {
    return <FormControl> this.samplingNeeds.get('tissueType');
  }

  public get tissueCollectionType(): FormControl {
    return <FormControl> this.samplingNeeds.get('tissueCollectionType');
  }

  public get samplingDateCalculationStrategy(): FormControl {
    return <FormControl> this.samplingNeeds.get('samplingDateCalculationStrategy');
  }

  public get growthStage(): FormControl {
    return <FormControl> this.samplingNeeds.get('growthStage');
  }

  public get daysAfterPlanting(): FormControl {
    return <FormControl> this.samplingNeeds.get('daysAfterPlanting');
  }

  public get displayTissueCollectionType(): boolean {
    return this.assay.valid && this.tissueType.valid;
  }

  public get displaySamplingDateCalculationFields(): boolean {
    return this.displayTissueCollectionType && this.tissueCollectionType.value === TissueCollectionType.Custom;
  }

  public get displayGrowthStage(): boolean {
    return this.samplingDateCalculationStrategy.value === SamplingDateCalculationStrategy.GrowthStage;
  }

  public get displayDaysAfterPlanting(): boolean {
    return this.samplingDateCalculationStrategy.value === SamplingDateCalculationStrategy.DaysAfterPlanting;
  }

  public get displaySamplingDate(): boolean {
    return this.displayDaysAfterPlanting && this.daysAfterPlanting.valid;
  }
  
  private get doesSamplingConfigurationRuleExist(): boolean {
    return this.projectedTaskConfigurations.some(s => s.laboratoryAssayId === this.assay.value && s.tissueTypeId === this.tissueType.value);
  }

  public get filteredTissueCollectionTypes(): SamplingTissueCollectionTypeViewModel[] {
    if (this.displayTissueCollectionType && this.doesSamplingConfigurationRuleExist)
      return this.tissueCollectionTypes;

    return this.tissueCollectionTypes.filter(t => t.id === TissueCollectionType.Custom);
  }

  public get samplingDate(): Date {
    let samplingDateOffset: number = this.daysAfterPlanting.value;
    
    if (this.requestDetails.crop === this.settings.wheatCropId) {
      samplingDateOffset += this.settings.NumberOfDaysInDormancy;
    }

    return addDays(this.requestDetails.plantingDate, samplingDateOffset);
  }

  constructor(private settingsService: SettingsService) {
    this.settings = this.settingsService.get<Settings>("_appSettings");
  }

  ngOnInit() {
    this.assay.valueChanges.subscribe(() => this.updateTissueCollectionType());
    this.tissueType.valueChanges.subscribe(() => this.updateTissueCollectionType());

    this.tissueCollectionType.valueChanges.subscribe((t: TissueCollectionType) => this.tissueCollectionTypeValueChanged(t));

    this.samplingDateCalculationStrategy.valueChanges.subscribe((s: SamplingDateCalculationStrategy) => this.updateSamplingDateCalculationFields(s));
  }

  private updateTissueCollectionType() {
    let defaultTissueCollectionType: TissueCollectionType = TissueCollectionType.Custom;
    if (this.doesSamplingConfigurationRuleExist) {
      defaultTissueCollectionType = TissueCollectionType.Standard;
    }
    this.tissueCollectionType.setValue(defaultTissueCollectionType);
  } 

  private tissueCollectionTypeValueChanged(tissueCollectionType: TissueCollectionType) {
    if (tissueCollectionType === TissueCollectionType.Standard) {
      this.clearGrowthStage();
      this.clearDaysAfterPlanting();
    }
    else {
      this.updateSamplingDateCalculationFields(this.samplingDateCalculationStrategy.value);
    }
  }

  private updateSamplingDateCalculationFields(samplingDateCalculationStrategy: SamplingDateCalculationStrategy) {
    if (samplingDateCalculationStrategy === SamplingDateCalculationStrategy.GrowthStage) {
      this.enableGrowthStage();
      this.clearDaysAfterPlanting();
    }
    else {
      this.clearGrowthStage();
      this.enableDaysAfterPlanting();
    }
  }

  private clearGrowthStage() {
    this.growthStage.setValue(null);
    this.growthStage.clearValidators();
    this.growthStage.disable();
    this.growthStage.updateValueAndValidity();
  }

  private clearDaysAfterPlanting() {
    this.daysAfterPlanting.setValue(null);
    this.daysAfterPlanting.clearValidators();
    this.daysAfterPlanting.disable();
    this.daysAfterPlanting.updateValueAndValidity();
  }

  private enableGrowthStage() {
    this.growthStage.enable();
    this.growthStage.setValidators(Validators.required);
    this.growthStage.updateValueAndValidity();
  }

  private enableDaysAfterPlanting() {
    this.daysAfterPlanting.enable();
    this.daysAfterPlanting.setValidators([ Validators.required, Validators.min(0) ]);
    this.daysAfterPlanting.updateValueAndValidity();
  }
}

export interface SamplingTissueTypeViewModel {
  id: number;
  display: string;
}

export interface SamplingGrowthStageViewModel {
  id: number;
  display: string;
}

export interface SamplingAssayViewModel {
  id: number;
  display: string;
}

export interface SamplingLaboratoryViewModel {
  id: number;
  display: string;
}

export interface SamplingTissueCollectionTypeViewModel {
  id: TissueCollectionType;
  display: string;
}

export enum SamplingDateCalculationStrategy {
  GrowthStage,
  DaysAfterPlanting
}


// WEBPACK FOOTER //
// F:/PHAZP5152IS01-4/_work/202/s/src/app/request-creation/experiment-design/sampling-needs/sampling-needs-form/sampling-needs-form.component.ts